var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiInstance } from '@/api';
import { Icon } from '@/assets/icons/LibrarySvg';
import { panelSurface, tileButton } from '@/components/Board/commonToolbarStyles';
import { useAuth } from '@/context/AuthContext';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotification } from '@/hooks/useNotification';
import NoteDetail from './NoteDetail';
import NoteListItem from './NoteListItem';
const NotesPanel = ({ styles, onNoteUpdate, sendNoteUpdate, onClose }) => {
    const { user } = useAuth();
    const { noteApiInstance } = useApiInstance();
    const { showError } = useNotification();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const createdByFilter = useMemo(() => ((user === null || user === void 0 ? void 0 : user.id) ? { created_by: user.id } : undefined), [user]);
    const debouncedSearchQuery = useDebounce(searchQuery, 400);
    /**
     * Фильтрует заметки по поисковому запросу.
     * Поиск выполняется по названию заметки и названиям тегов.
     */
    const filteredNotes = useMemo(() => {
        if (!debouncedSearchQuery.trim()) {
            return notes;
        }
        const query = debouncedSearchQuery.toLowerCase().trim();
        return notes.filter((note) => {
            var _a, _b;
            const nameMatch = note.name.toLowerCase().includes(query);
            const tagsMatch = (_b = (_a = note.tags) === null || _a === void 0 ? void 0 : _a.some((tag) => tag.name.toLowerCase().includes(query))) !== null && _b !== void 0 ? _b : false;
            return nameMatch || tagsMatch;
        });
    }, [notes, debouncedSearchQuery]);
    /**
     * Обработчик обновления заметки из WebSocket.
     * Обновляет заметку в списке и выбранную заметку, если она была изменена.
     */
    const handleNoteUpdate = useCallback((note) => {
        setNotes((prev) => prev.map((n) => (n.id === note.id ? note : n)));
        // Обновляем выбранную заметку, если она была изменена
        if ((selectedNote === null || selectedNote === void 0 ? void 0 : selectedNote.id) === note.id) {
            setSelectedNote(note);
        }
    }, [selectedNote]);
    /**
     * Регистрируем callback для получения обновлений заметок из WebSocket.
     * Callback вызывается из BoardPage при получении сообщения note.updated.
     */
    useEffect(() => {
        onNoteUpdate === null || onNoteUpdate === void 0 ? void 0 : onNoteUpdate(handleNoteUpdate);
    }, [onNoteUpdate, handleNoteUpdate]);
    useEffect(() => {
        let mounted = true;
        const load = () => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            try {
                const list = yield noteApiInstance.list(createdByFilter);
                if (mounted) {
                    setNotes(list);
                }
            }
            finally {
                setLoading(false);
            }
        });
        load();
        return () => {
            mounted = false;
        };
    }, [noteApiInstance, createdByFilter]);
    const handleDelete = (id) => __awaiter(void 0, void 0, void 0, function* () {
        yield noteApiInstance.delete(id);
        setNotes((prev) => prev.filter((n) => n.id !== id));
        if ((selectedNote === null || selectedNote === void 0 ? void 0 : selectedNote.id) === id) {
            setSelectedNote(null);
        }
    });
    const handleCreateClick = () => {
        setIsCreating(true);
        setSelectedNote(null);
    };
    const handleCancel = () => {
        setIsCreating(false);
        setSelectedNote(null);
    };
    const handleSave = (name, text) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        setIsSaving(true);
        try {
            if (isCreating) {
                const newNote = yield noteApiInstance.create({
                    name,
                    text,
                    tagIds: [],
                    linkedNoteIds: [],
                });
                setNotes((prev) => [newNote, ...prev]);
                setIsCreating(false);
                setSelectedNote(newNote);
            }
            else if (selectedNote) {
                const updated = yield noteApiInstance.update(selectedNote.id, { name, text });
                setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
                setSelectedNote(updated);
                // Отправляем обновление через WebSocket для синхронизации с текстовыми элементами на доске
                sendNoteUpdate === null || sendNoteUpdate === void 0 ? void 0 : sendNoteUpdate({
                    id: updated.id,
                    name: updated.name,
                    text: updated.text,
                });
            }
        }
        catch (error) {
            const axiosError = error;
            const ok = ((_a = axiosError === null || axiosError === void 0 ? void 0 : axiosError.response) === null || _a === void 0 ? void 0 : _a.status) === 200 || ((_b = axiosError === null || axiosError === void 0 ? void 0 : axiosError.response) === null || _b === void 0 ? void 0 : _b.status) === 201;
            const errorMessage = ok
                ? undefined
                : isCreating
                    ? 'Ошибка при создании заметки'
                    : 'Ошибка при сохранении заметки';
            if (errorMessage) {
                showError(errorMessage);
            }
            console.error('Error saving note:', error);
        }
        finally {
            setIsSaving(false);
        }
    });
    const handleBack = () => {
        setSelectedNote(null);
        setIsCreating(false);
    };
    return (_jsxs("div", { style: panelSurface(Object.assign({ position: 'fixed', right: 16, top: 16, display: 'flex', flexDirection: 'column', gap: 0, flex: 1, maxWidth: 680, minWidth: 480, width: '100%', minHeight: '50%', height: 'calc(100vh - 100px)', padding: 0, pointerEvents: 'auto', background: '#1a1d21', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)' }, styles)), "aria-label": "\u0417\u0430\u043C\u0435\u0442\u043A\u0438 \u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044F", children: [_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 20px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    gap: 12,
                }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 12, flex: 1 }, children: [_jsx("div", { style: { fontWeight: 600, color: '#e8eaed' }, children: "\u0417\u0430\u043C\u0435\u0442\u043A\u0438" }), !isCreating && !selectedNote && (_jsx("button", { type: "button", onClick: handleCreateClick, "aria-label": "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u043D\u043E\u0432\u0443\u044E \u0437\u0430\u043C\u0435\u0442\u043A\u0443", style: Object.assign(Object.assign({}, tileButton(false, { padding: '8px 12px', minWidth: 40, height: 36 })), { background: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#e8eaed', fontWeight: 400, transition: 'all 0.2s ease' }), onMouseEnter: (e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                }, children: "+" }))] }), !isCreating && !selectedNote && (_jsxs("div", { style: {
                            position: 'relative',
                            flex: 1,
                            maxWidth: 280,
                        }, children: [_jsx("input", { type: "text", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), placeholder: "\u041F\u043E\u0438\u0441\u043A...", "aria-label": "\u041F\u043E\u0438\u0441\u043A", style: {
                                    width: '100%',
                                    fontSize: 14,
                                    color: '#e8eaed',
                                    background: 'rgba(255, 255, 255, 0.04)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: 8,
                                    padding: '8px 36px 8px 12px',
                                    outline: 'none',
                                    transition: 'all 0.15s ease',
                                    height: 36,
                                }, onFocus: (e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                                }, onBlur: (e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                } }), searchQuery && (_jsx("button", { type: "button", onClick: () => setSearchQuery(''), "aria-label": "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C \u043F\u043E\u0438\u0441\u043A", style: Object.assign(Object.assign({ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }, tileButton(false, { padding: 4, minWidth: 24, height: 24 })), { background: 'transparent', border: 'none', color: '#9aa0a6', opacity: 0.6, transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }), onMouseEnter: (e) => {
                                    e.currentTarget.style.opacity = '1';
                                    e.currentTarget.style.color = '#e8eaed';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.opacity = '0.6';
                                    e.currentTarget.style.color = '#9aa0a6';
                                }, children: _jsx(Icon, { name: "cross" }) }))] })), onClose && (_jsx("button", { type: "button", onClick: onClose, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043F\u0430\u043D\u0435\u043B\u044C \u0437\u0430\u043C\u0435\u0442\u043E\u043A", style: Object.assign(Object.assign({}, tileButton(false, { padding: 8, minWidth: 36, height: 36 })), { background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#9aa0a6', opacity: 0.7, transition: 'all 0.15s ease' }), onMouseEnter: (e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.background = 'rgba(220, 38, 38, 0.15)';
                            e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.3)';
                            e.currentTarget.style.color = '#ef4444';
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.opacity = '0.7';
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.color = '#9aa0a6';
                        }, children: _jsx(Icon, { name: "cross" }) }))] }), _jsx("div", { style: {
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    flex: 1,
                    overflowY: 'auto',
                }, children: loading ? (_jsx("div", { style: {
                        color: '#9aa0a6',
                        fontSize: 14,
                        padding: '24px 20px',
                        textAlign: 'center',
                    }, children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" })) : isCreating ? (_jsx(NoteDetail, { note: null, isCreating: true, isSaving: isSaving, onBack: handleBack, onCancel: handleCancel, onSave: handleSave })) : selectedNote ? (_jsx(NoteDetail, { note: selectedNote, isSaving: isSaving, onBack: handleBack, onSave: handleSave })) : filteredNotes.length === 0 ? (_jsx("div", { style: {
                        color: '#9aa0a6',
                        fontSize: 14,
                        padding: '40px 20px',
                        textAlign: 'center',
                    }, children: debouncedSearchQuery.trim() ? 'Заметки не найдены' : 'Нет заметок' })) : (_jsx("div", { style: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }, children: filteredNotes.map((n) => (_jsx(NoteListItem, { note: n, isSelected: false, onSelect: setSelectedNote, onDelete: handleDelete }, n.id))) })) })] }));
};
export default NotesPanel;
